import { ExIcon, ExLeftmenubarAdjustable, ExLeftmenubarLink } from '@boomi/exosphere';
import { AISearch, Header } from '@boomi/magnetosphere';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { getAuthFromWindow } from '../authentication/authUtils';
import { PLATFORM_SERVER_BASE_URL } from '../config';
import { appRoutes } from '../routes';
import Unauthorized from '../unauthorized/Unauthorized';
import styles from './AppShell.module.css';
import { setHeaderHeight } from './headerHeight';

const getIsMenuCollapsed = () => localStorage.getItem('menuCollapsed') === 'true';

const platformUrl = new URL(PLATFORM_SERVER_BASE_URL).hostname;

const removeCookie = (name: string) => cookieStore.delete({ name });

const pageInfo = {
    name: 'Connect',
    homePage: { url: '/', title: 'Connect' },
    redirect: { url: '/', title: 'Connect' },
};

const AppShell = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [isMenuCollapsed] = useState(getIsMenuCollapsed);
    const [jwt, setJwt] = useState('');
    const [isAiSearchOpen, setIsAiSearchOpen] = useState(false);
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const el = headerRef.current;
        if (!el) return;

        const update = () => setHeaderHeight(el.getBoundingClientRect().height);

        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        const auth = getAuthFromWindow();
        if (!auth) return;

        auth.getToken().then((token) => {
            setJwt(token);
        });

        auth.getAccountId().then((accountId) => {
            cookieStore.set({ name: 'ACCOUNT_ID', value: accountId });
        });
    }, []);

    useEffect(() => {
        const handler = () => setIsUnauthorized(true);
        window.addEventListener('app:unauthorized', handler);
        return () => window.removeEventListener('app:unauthorized', handler);
    }, []);

    const routeToAi = useCallback((isClickedAi: boolean) => {
        if (isClickedAi) {
            const auth = getAuthFromWindow();
            if (!auth) return;

            auth.getAccountId().then((accountId) => {
                window.open(
                    `${window.location.protocol}//${platformUrl}/BoomiAI.html#ai;accountId=${accountId}`,
                    '_blank',
                );
            });
        }
    }, []);

    const handleSignOut = useCallback(async () => {
        removeCookie('ACCOUNT_ID');
        try {
            await getAuthFromWindow()?.logout();
        } catch {
            // continue to login even if server logout fails
        }
        navigate('/');
    }, [navigate]);

    const handleSwitchAccountSuccess = useCallback(async ({ accountId }: { accountId: string }) => {
        removeCookie('ACCOUNT_ID');
        const auth = getAuthFromWindow();
        await auth?.logout();
        await auth?.getToken(accountId);
        window.location.reload();
    }, []);

    const handleAuthError = useCallback(() => {
        removeCookie('ACCOUNT_ID');
        navigate('/');
    }, [navigate]);

    return (
        <div className={styles.shell}>
            <div ref={headerRef}>
                <Header
                    pageInfo={pageInfo}
                    checkForDevelopment={process.env['NODE_ENV'] === 'development'}
                    platformUrl={platformUrl}
                    showBoomiAi={routeToAi}
                    searchIconEventHandler={() => setIsAiSearchOpen((prev) => !prev)}
                    onSignOutSuccess={handleSignOut}
                    onSwitchAccountSuccess={handleSwitchAccountSuccess}
                    onAuthError={handleAuthError}
                    disableTranslation
                    suppressDefaultLandingPage
                    noNeedAuthorization
                    jwt={jwt}
                />
            </div>
            <AISearch
                openSearchPanel={isAiSearchOpen}
                userIsAuthenticated
                closeBtnEventHandler={() => setIsAiSearchOpen(false)}
                jwt={jwt}
            />
            {isUnauthorized ? (
                <main className={`${styles.main} ${styles.unauthorizedMain}`}>
                    <Unauthorized />
                </main>
            ) : (
                <div className={styles.content}>
                    <ExLeftmenubarAdjustable
                        isCollapse={isMenuCollapsed}
                        adjustable
                        animated
                        expandWidth={264}
                        collapseWidth={56}
                        // Detail is nested in another detail for some reason, so we have to do (ev.detail as unknown ...).detail.collapse to get the value
                        onCollapse={(ev) =>
                            localStorage.setItem(
                                'menuCollapsed',
                                String(
                                    (ev.detail as unknown as { detail: { collapse: boolean } })
                                        .detail.collapse,
                                ),
                            )
                        }
                    >
                        {appRoutes.map(({ path, label, exIcon }) => (
                            <ExLeftmenubarLink
                                key={path}
                                data-pendo-link={`appshell-leftmenubar-${path}`}
                                label={label}
                                tooltipText={label}
                                selected={pathname.startsWith(`/${path}`)}
                                onItemSelection={() => navigate(`/${path}`)}
                            >
                                <ExIcon slot="icon" icon={exIcon} />
                            </ExLeftmenubarLink>
                        ))}
                    </ExLeftmenubarAdjustable>
                    <div className={styles.mainArea}>
                        <main className={styles.main}>
                            <Outlet />
                        </main>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppShell;
